import {
  Box,
  Children,
  ErrorBanner,
  FileContent,
  Loader,
  LocalizedString,
  ValueOf,
} from "@dbn/omnichannel-design-system-g4p";
import { useEffect, useState } from "react";
import { RuntimeLocaleKey, useFormatMessage } from "../../intl";
import * as Styles from "./DocumentViewer.css";
import { PdfViewer as InnerPdfViewer } from "../../Common/Pdf/PdfViewer";
import { useOrientation, useUpdateEffect } from "react-use";

export const DocumentType = {
  Pdf: "application/pdf",
  // Image.
  Jpeg: "image/jpeg",
  Png: "image/png",
  Tiff: "image/tiff",
  // Office.
  Docx:
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  Xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  // Outlook.
  Msg: "application/vnd.ms-outlook",
  Msg2: ".msg",
} as const;

export type DocumentType = ValueOf<typeof DocumentType>;

export type DocumentModel = {
  name: string;
  docSet?: string;
  data: FileContent;
};

export type DocumentEnvelopeModel = DocumentModel & {
  type: DocumentType;
};

export type DocumentViewerProps = DocumentEnvelopeModel & {
  fallback?: (props: DocumentEnvelopeModel) => Children;
  onSupportDetected?: (supported: boolean) => void;
  onContentLoaded?: (numPages?: number) => unknown;
  onLastPageReach?: () => void;
  ariaLabelScrollDown?: LocalizedString;
  minHeight?: string;
};

export function DocumentViewer(props: DocumentViewerProps) {
  const { type, data, fallback, onSupportDetected, ...otherProps } = props;
  const [isTypeSupported, setIsTypeSupported] = useState(true);

  useEffect(() => {
    // By default we assume a media type is supported. In case of error there
    // will be an handler which overwrites this assumption.
    // For example, TIFF image format is supported by IE and Edge Legacy on
    // Windows, and by Safari on MacOS, but not by other browsers.
    setIsTypeSupported(true);
  }, [type, data]);

  useEffect(() => {
    if (!onSupportDetected) {
      return;
    }

    onSupportDetected(isTypeSupported);
  }, [isTypeSupported, onSupportDetected]);

  useEffect(() => {
    if (isTypeSupported) {
      return;
    }

    logMediaUnsupported(type);
  }, [isTypeSupported, type]);

  if (!isTypeSupported) {
    return <>{fallback && fallback({ ...otherProps, type, data })}</>;
  }

  switch (type) {
    case DocumentType.Pdf: {
      return (
        <PdfViewer
          {...otherProps}
          data={data}
          onError={() => setIsTypeSupported(false)}
          minHeight={props.minHeight}
        />
      );
    }
    case DocumentType.Jpeg:
    case DocumentType.Png:
    case DocumentType.Tiff: {
      return (
        <ImageViewer
          {...otherProps}
          type={type}
          data={data}
          onError={() => setIsTypeSupported(false)}
          onLoad={() => setIsTypeSupported(true)}
        />
      );
    }
  }

  setIsTypeSupported(false);

  return null;
}

export type PdfViewerProps = DocumentModel & {
  onError?: () => void;
  onContentLoaded?: (numPages?: number) => unknown;
  onLastPageReach?: () => void;
  ariaLabelScrollDown?: LocalizedString;
  minHeight?: string;
};

export function PdfViewer(props: PdfViewerProps) {
  const [pdfLoading, setPdfLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [key, setkey] = useState(Math.random());
  const formatMessage = useFormatMessage();
  const deviceOrientation = useOrientation();

  const handleOnContentLoaded = (numPages?: number) => {
    setPdfLoading(false);
    props.onContentLoaded && props.onContentLoaded(numPages);
  };

  const handleOnError = () => {
    setPdfLoading(false);
    setHasError(true);
    props.onError && props.onError();
  };

  useEffect(() => {
    setPdfLoading(true);
    setHasError(false);
    setkey(Math.random());
  }, [props.data]);

  useUpdateEffect(() => {
    setkey(Math.random());
  }, [deviceOrientation.type]);

  const altTextKey = mapAltTextKey(props.name);
  const altText =
    altTextKey !== ""
      ? formatMessage(altTextKey as RuntimeLocaleKey)
      : props.docSet
      ? formatMessage(`documents.pdf.alt.${props.docSet}` as RuntimeLocaleKey)
      : "";

  return (
    <Box grow style={{ position: "relative", overflowX: "hidden" }}>
      {pdfLoading && <DocumentLoading />}
      {!pdfLoading && hasError && <DocumentError />}
      <InnerPdfViewer
        data={props.data}
        onPdfLoaded={handleOnContentLoaded}
        onPdfError={handleOnError}
        onLastPageReach={props.onLastPageReach}
        key={key}
        pdfAltText={altText}
        ariaLabelScrollDown={props.ariaLabelScrollDown}
        minHeight={props.minHeight}
      />
    </Box>
  );
}

export type ImageViewerProps = DocumentEnvelopeModel & {
  onError?: () => void;
  onLoad?: () => void;
};

export function ImageViewer(props: ImageViewerProps) {
  const { type, data, onError, onLoad } = props;

  return (
    <div style={{ display: "block" }}>
      <img
        className={Styles.image}
        src={dataUrlFor(type, data)}
        onLoad={onLoad}
        onError={onError}
        alt={props.name}
      />
    </div>
  );
}

export type DownloadDocumentButtonProps = DocumentEnvelopeModel;

export function DocumentLoading() {
  const formatMessage = useFormatMessage();
  return (
    <Box hAlignContent="center" basis="100%" vAlignContent="center">
      <Loader
        ariaLabelMessage={formatMessage(
          "generic.loading.alt" as RuntimeLocaleKey
        )}
      />
    </Box>
  );
}

export function DocumentError() {
  const formatMessage = useFormatMessage();

  return (
    <ErrorBanner>{formatMessage("DocumentViewer.DisplayError")}</ErrorBanner>
  );
}

export function dataUrlFor(mimeType: string, data: string) {
  return `data:${mimeType};base64,` + data;
}

export function logMediaUnsupported(mimeType: string) {
  console.log(
    "DocumentViewer:",
    "browser seams to not support mimetype",
    mimeType
  );
}

export function mapAltTextKey(doc: string): string {
  switch (doc) {
    case "Informácie o spotrebiteľskom úvere":
      return "documents.pdf.alt.PRECONTRACT_CASHLOAN";
    case "Zaznam o digitalni aktivite":
      return "documents.pdf.alt.FINAL_DAR_CASHLOAN";
    case "N/A":
      return "documents.pdf.alt.CLIENT_DOCUMENT_STANDARDLOAN";
    case "Rámcová zmluva":
      return "documents.pdf.alt.FRAMEWORK_AGREEMENT_STANDARDLOAN";
    case "Zmluva o produkte":
      return "documents.pdf.alt.ACCOUNT_PRODUCT_CONTRACT_STANDARDLOAN";
    case "Informácie o spotrebiteľskom úveru":
      return "documents.pdf.alt.PRECONTRACT_STANDARDLOAN";
    case "Zaznam o sprostredkovaní poistenia":
      return "documents.pdf.alt.INSURANCE_RECORD_LIST_STANDARDLOAN";
    case "Informačný formulár k poisteniu":
      return "documents.pdf.alt.CPI_COST_INFORMATION";
    case "Prihláška do poistenia":
      return "documents.pdf.alt.INSURANCE_APPLICATION_FORM_STANDARDLOAN";
    case "Zmluva o PRESTO Úvere":
      return "documents.pdf.alt.LOAN_PRODUCT_CONTRACT_STANDARDLOAN";
    case "Potvrdenie o príjme zo závislej činnosti (♒ Potřeba odstranit z názvu (Žiadateľ))":
      return "documents.pdf.alt.PROOF_OF_INCOME";
    case "Súhlas so spracovaním osobných údajov":
      return "documents.pdf.alt.GDPR_CONSENT";
    case "Rámcová smlouva":
      return "documents.pdf.alt.FRAMEWORK_AGREEMENT";
    case "Smlouva o produktu":
      return "documents.pdf.alt.PRODUCT_CONTRACT";
    case "Smlouva o Kontokorentním úvěru":
      return "documents.pdf.alt.LOAN_PRODUCT_CONTRACT_OVERDRAFT";
    case "Zmluva o povoleném prečerpanie":
      return "documents.pdf.alt.LOAN_PRODUCT_CONTRACT_OVERDRAFT";
    case "Overdraft Product Contract":
      return "documents.pdf.alt.LOAN_PRODUCT_CONTRACT_OVERDRAFT";
    case "Product agreement":
      return "documents.pdf.alt.PRODUCT_AGREEMENT";
  }
  return "";
}
